type Folded<ERR, VAL, TO> = {
  error: (error: ERR) => TO;
  success: (value: VAL) => TO;
};

export const VOID = Symbol('Void');

export type Void = typeof VOID;

export abstract class Result<ERR, VAL> {
  static error<ERR, VAL>(error: ERR): Err<ERR, VAL> {
    return new Err(error);
  }

  static success<ERR, VAL>(value: VAL): Success<ERR, VAL> {
    return new Success(value);
  }

  abstract fold<TO>(folded: Folded<ERR, VAL, TO>): TO;

  abstract map<TO>(transform: (value: VAL) => TO): Result<ERR, TO>;

  abstract flatMap<TO = VAL>(transform: (value: VAL) => Result<ERR, TO>): Result<ERR, TO>;

  abstract orThrow(): VAL;

  abstract isError(): this is Err<ERR, VAL>;

  abstract isSuccess(): this is Success<ERR, VAL>;
}

class Err<ERR, VAL> extends Result<ERR, VAL> {
  constructor(private readonly error: ERR) {
    super();
  }

  override fold<TO>(folded: Folded<ERR, VAL, TO>): TO {
    return folded.error(this.error);
  }

  override map<TO>(_: (value: VAL) => TO): Result<ERR, TO> {
    return Result.error(this.error);
  }

  override flatMap<TO = VAL>(_: (value: VAL) => Result<ERR, TO>): Result<ERR, TO> {
    return Result.error(this.error);
  }

  override orThrow(): VAL {
    throw this.error;
  }

  override isError(): this is Err<ERR, VAL> {
    return true;
  }

  override isSuccess(): this is Success<ERR, VAL> {
    return false;
  }
}

class Success<ERR, VAL> extends Result<ERR, VAL> {
  constructor(private readonly value: VAL) {
    super();
  }

  override fold<TO>(folded: Folded<ERR, VAL, TO>): TO {
    return folded.success(this.value);
  }

  override map<TO>(transform: (value: VAL) => TO): Result<ERR, TO> {
    return Result.success(transform(this.value));
  }

  override flatMap<TO = VAL>(transform: (value: VAL) => Result<ERR, TO>) {
    return transform(this.value);
  }

  override orThrow(): VAL {
    return this.value;
  }

  override isError(): this is Err<ERR, VAL> {
    return false;
  }

  override isSuccess(): this is Success<ERR, VAL> {
    return true;
  }
}
